import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { FiStar } from 'react-icons/fi';
import api from '../../../../lib/api';
import { extractApiError } from '../../../../lib/apiError';
import { todayForInput } from '../../../../lib/dateUtils';
import CloseButton from '../../../../components/ui/CloseButton';
import Button from '../../../../components/ui/Button';
import AddAssetPrefilledRows from './AddAssetPrefilledRows';
import {
  ELECTRICAL_ASSET_TYPES,
  FITMENTS_ASSET_TYPES,
  buildInitialFormData,
  assetTypeChangePatch,
  buildAssetPayload,
  usesBoardCircuitFields,
  usesCircuitBreakerNumber,
} from '../../../../lib/assetCreation';

/**
 * Full-screen mobile "Add asset — smart defaults" form.
 *
 * Pre-fills everything from the board/group context and the last asset added, so the
 * only real inputs are the ones unique to the device: CB number + capacity on a board
 * or RCD, equipment code on anything else (see usesBoardCircuitFields).
 *
 * Props:
 *   open           — controlled open state
 *   onClose        — close handler
 *   siteId         — locked session site id
 *   siteName       — site display name (shown in scope line + SITE row)
 *   elementType    — 'electrical' | 'fitments' (locked from session)
 *   sessionAssetType — session asset type, defaults the Asset Type row
 *   defaultAssetType — group-derived individual asset type (e.g. 'rcd' for a DB
 *                      group); overrides the sessionAssetType-based default so a
 *                      circuit added to DB.1 never falls back to exit_light
 *   boardContext   — { dbNumber, building, room, manufacturer, model }
 *   groupLabel     — group the form was opened from; scopes the header line when
 *                    there is no dbNumber (i.e. any non-board group)
 *   onSuccess      — called with the created asset (updates "last added")
 */
export default function AddAssetSmartForm({
  open,
  onClose,
  siteId,
  siteName,
  elementType,
  sessionAssetType,
  defaultAssetType = '',
  boardContext = {},
  groupLabel = '',
  onSuccess,
}) {
  const queryClient = useQueryClient();

  const makeDraft = () => {
    const base = buildInitialFormData(elementType, sessionAssetType);
    // A group-derived default (e.g. 'rcd' for a DB group) wins over the
    // sessionAssetType default, which can resolve to exit_light when the session
    // asset type is briefly empty. assetTypeChangePatch recomputes the dependent
    // element/component/frequency fields so they stay consistent with the type.
    const typed = defaultAssetType
      ? { ...base, ...assetTypeChangePatch(defaultAssetType) }
      : base;
    return {
      ...typed,
      dbNumber: boardContext.dbNumber ?? typed.dbNumber,
      building: boardContext.building ?? typed.building,
      room: boardContext.room ?? typed.room,
      manufacturer: boardContext.manufacturer ?? typed.manufacturer,
      model: boardContext.model ?? typed.model,
      installationDate: todayForInput(),
      conditionRating: '3',
    };
  };

  const [formData, setFormData] = useState(makeDraft);
  // Building/Room requirement is surfaced inline (not just as a toast): the two
  // rows get an error state and the first is scrolled into view when Save is
  // blocked. Cleared as soon as the tech types a location.
  const [locationError, setLocationError] = useState(false);
  const buildingRowRef = useRef(null);

  const set = (field) => (e) => {
    const { value } = e.target;
    setFormData((prev) => ({ ...prev, [field]: value }));
    if ((field === 'building' || field === 'room') && value.trim()) {
      setLocationError(false);
    }
  };

  const handleAssetTypeChange = (e) => {
    const nextType = e.target.value;
    setFormData((prev) => {
      const patch = { ...prev, ...assetTypeChangePatch(nextType) };
      // A distribution board is its own board: it must not inherit the parent
      // group's board number (that nests the new board inside the old one) and it
      // has no circuit breaker. Clear both so the tech names the new board via the
      // DB NUMBER field. Switching (back) to a circuit restores the group board.
      if (nextType === 'distribution_board') {
        return { ...patch, dbNumber: '', circuitBreakerNumber: '' };
      }
      if (nextType === 'rcd') {
        return { ...patch, dbNumber: prev.dbNumber || boardContext.dbNumber || '' };
      }
      return patch;
    });
  };

  const createMutation = useMutation(
    (data) => api.post('/assets', data),
    {
      onSuccess: (res) => {
        // Invalidate the whole sessionAssets family so every paged query key
        // (['sessionAssets', siteId, search, page, …]) is refetched, not just the
        // one currently mounted. onSuccess below also resets the pager to page 1
        // so a newly-created asset that sorts onto an earlier page still appears.
        queryClient.invalidateQueries(['sessionAssets']);
        onSuccess?.(res.data);
      },
      onError: (err) => toast.error(extractApiError(err)),
    }
  );

  const save = (addAnother) => {
    // Backend requires Building OR Room (assets.js). Surface it inline on the two
    // rows AND as a toast, and scroll the first offending field into view — a
    // fading toast alone is a poor pointer to fields below the fold, especially
    // when Add Asset is opened from the Session Summary sheet with no board scope.
    if (!formData.building?.trim() && !formData.room?.trim()) {
      setLocationError(true);
      if (typeof buildingRowRef.current?.scrollIntoView === 'function') {
        buildingRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      toast.error('Building or Room is required');
      return;
    }
    createMutation.mutate(buildAssetPayload(formData, { siteId, elementType }), {
      onSuccess: () => {
        if (addAnother) {
          toast.success('Asset added — ready for the next');
          // Clear every per-device identifier, whichever shape the type uses —
          // carrying one over would duplicate it onto the next asset.
          setFormData((prev) => ({
            ...prev,
            circuitBreakerNumber: '',
            capacity: '',
            equipmentCode: '',
            serialNumber: '',
          }));
        } else {
          toast.success('Asset added to session');
          onClose?.();
        }
      },
    });
  };

  if (!open) return null;

  const assetTypeOptions =
    elementType === 'fitments' ? FITMENTS_ASSET_TYPES : ELECTRICAL_ASSET_TYPES;
  const isBoardOrRcd = usesBoardCircuitFields(formData.assetType);
  // An RCD needs its circuit breaker number; a distribution board needs its own
  // board number (DB NUMBER). Only these carry board identity, so only these show
  // capacity. Everything else is identified by its equipment code.
  const isRcd = usesCircuitBreakerNumber(formData.assetType);
  const isDistributionBoard = formData.assetType === 'distribution_board';
  // Outside a DB/RCD group there is deliberately no dbNumber — we gated it so
  // exit-light groups stop writing junk board numbers into db_number. Fall back to
  // the group the form was opened from, and to the site alone when there is no
  // group at all (opened from the Session Summary sheet). Never a bare dash, and
  // never by putting the group label back into boardContext.dbNumber.
  const siteLabel = (siteName || '').toUpperCase();
  const scope = boardContext.dbNumber || groupLabel || '';
  const scopeLine = scope
    ? `SCOPED TO ${scope} · ${siteLabel}`
    : `SCOPED TO ${siteLabel}`;
  const saving = createMutation.isLoading;

  return createPortal(
    <div className="add-asset-form" role="dialog" aria-modal="true" aria-label="Add asset">

      {/* Dark header */}
      <header className="add-asset-form__header">
        <div className="add-asset-form__header-titles">
          <h2 className="add-asset-form__title">Add asset</h2>
          <p className="add-asset-form__scope">{scopeLine}</p>
        </div>
        <CloseButton onClick={onClose} ariaLabel="Close add asset" className="add-asset-form__close" />
      </header>

      {/* Scrollable body */}
      <div className="add-asset-form__body">

        {/* Hint banner */}
        <div className="add-asset-form__hint">
          <FiStar size={16} aria-hidden className="add-asset-form__hint-icon" />
          <p className="add-asset-form__hint-text">
            {isDistributionBoard ? (
              <>
                Pre-filled from this group and the last asset you added. Only the{' '}
                <strong>DB number</strong> and <strong>capacity</strong> need input.
              </>
            ) : isBoardOrRcd ? (
              <>
                Pre-filled from this board and the last asset you added. Only{' '}
                <strong>CB number</strong> and <strong>capacity</strong> need input.
              </>
            ) : (
              <>
                Pre-filled from this group and the last asset you added. Only the{' '}
                <strong>equipment code</strong> needs input.
              </>
            )}
          </p>
        </div>

        {/* Needs input — board/circuit identity for boards and RCDs, equipment
            code for every other type. An exit light has no circuit breaker. */}
        <p className="add-asset-form__section-label add-asset-form__section-label--input">NEEDS INPUT</p>
        {isBoardOrRcd ? (
          <div className="add-asset-form__needs-grid">
            <label className="add-asset-field">
              <span className="add-asset-field__label">
                {isDistributionBoard ? 'DB NUMBER' : 'CB NUMBER'}
              </span>
              {isDistributionBoard ? (
                <input
                  className="add-asset-field__input add-asset-field__input--gold"
                  value={formData.dbNumber}
                  onChange={set('dbNumber')}
                  placeholder="e.g. MSB.DB.4"
                />
              ) : (
                <input
                  className="add-asset-field__input add-asset-field__input--gold"
                  value={formData.circuitBreakerNumber}
                  onChange={set('circuitBreakerNumber')}
                  placeholder="e.g. CB6"
                />
              )}
            </label>
            <label className="add-asset-field">
              <span className="add-asset-field__label">CAPACITY</span>
              <input
                className="add-asset-field__input add-asset-field__input--gold"
                value={formData.capacity}
                onChange={set('capacity')}
                placeholder="e.g. 20A"
              />
            </label>
          </div>
        ) : (
          <label className="add-asset-field">
            <span className="add-asset-field__label">EQUIPMENT CODE</span>
            <input
              className="add-asset-field__input add-asset-field__input--gold"
              value={formData.equipmentCode}
              onChange={set('equipmentCode')}
              placeholder="e.g. A26"
            />
          </label>
        )}

        {/* Pre-filled */}
        <AddAssetPrefilledRows
          siteName={siteName}
          elementType={elementType}
          formData={formData}
          set={set}
          handleAssetTypeChange={handleAssetTypeChange}
          assetTypeOptions={assetTypeOptions}
          showDbNumberRow={isRcd}
          locationError={locationError}
          buildingRowRef={buildingRowRef}
        />

        {/* Notes */}
        <label className="add-asset-field add-asset-form__notes">
          <span className="add-asset-field__label">NOTES</span>
          <textarea
            className="add-asset-field__textarea"
            value={formData.notes}
            onChange={set('notes')}
            rows={3}
            placeholder="Optional"
          />
        </label>
      </div>

      {/* Sticky footer */}
      <footer className="add-asset-form__footer">
        <Button variant="primary" className="add-asset-form__save" onClick={() => save(false)} disabled={saving}>
          {saving ? 'Saving…' : 'Save asset'}
        </Button>
        <Button variant="outline" className="add-asset-form__save-another" onClick={() => save(true)} disabled={saving}>
          Save &amp; add another
        </Button>
      </footer>
    </div>,
    document.body,
  );
}
